import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from './Icon';

export { Icon };

export function Switch({ checked, onChange, disabled, label, title }) {
  const btn = (
    <button
      type="button" role="switch" aria-checked={!!checked} title={title}
      className={`k-switch${checked ? ' on' : ''}`} disabled={disabled}
      onClick={() => onChange(!checked)}
    />
  );
  if (!label) return btn;
  return (
    <label className="k-switch-row" onClick={(e) => { if (e.target.tagName !== 'BUTTON' && !disabled) onChange(!checked); }}>
      {btn}<span>{label}</span>
    </label>
  );
}

export function PageHead({ title, desc, children }) {
  return (
    <div className="k-page-head">
      <div>
        {title && <h1 className="k-page-title">{title}</h1>}
        {desc && <p className="k-page-desc" style={title ? undefined : { margin: 0 }}>{desc}</p>}
      </div>
      {children && <div className="k-row">{children}</div>}
    </div>
  );
}

export function Card({ title, sub, actions, children, pad = true, className = '', style }) {
  return (
    <section className={`k-card ${className}`} style={style}>
      {(title || actions) && (
        <div className="k-card-head">
          <div>
            {title && <h2 className="k-card-title">{title}</h2>}
            {sub && <p className="k-card-sub">{sub}</p>}
          </div>
          {actions && <div className="k-row">{actions}</div>}
        </div>
      )}
      <div className={pad ? 'k-card-pad' : ''}>{children}</div>
    </section>
  );
}

export function Empty({ icon = 'orders', title, children }) {
  return (
    <div className="k-empty">
      <Icon name={icon} size={34} strokeWidth={1.4} />
      {title && <strong>{title}</strong>}
      {children && <div>{children}</div>}
    </div>
  );
}

export function Loading({ text = 'Yükleniyor…' }) {
  return <div className="k-loading"><span className="k-spinner" />{text}</div>;
}

export function Modal({ title, onClose, children, footer, width = 520 }) {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  // Sayfanın en üst katmanında aç: yan menü gibi kapsayıcıların stilinden ve kırpmasından etkilenmesin.
  return createPortal(
    <div className="k-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <div className="k-modal" style={{ maxWidth: width }} role="dialog" aria-modal="true" aria-label={title}>
        <div className="k-modal-head">
          <h3 className="k-modal-title">{title}</h3>
          <button type="button" className="k-icon-btn" onClick={onClose} aria-label="Kapat"><Icon name="x" /></button>
        </div>
        <div className="k-modal-body">{children}</div>
        {footer && <div className="k-modal-foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

// ── Toast bildirimleri (alert yerine) ───────────────────────────────
const ToastContext = createContext(() => {});

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const push = useCallback((text, kind = 'info') => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, text, kind }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), kind === 'error' ? 6000 : 3500);
  }, []);
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className="k-toasts" aria-live="polite">
        {toasts.map((t) => <div key={t.id} className={`k-toast ${t.kind}`}>{t.text}</div>)}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}

// ── Sipariş durumları ───────────────────────────────────────────────
export const ORDER_STATUS = {
  pending: { label: 'Yeni', long: 'Onay bekliyor', tone: 'amber' },
  preparing: { label: 'Hazırlanıyor', long: 'Hazırlanıyor', tone: 'blue' },
  ready: { label: 'Hazır', long: 'Hazır (gel-al)', tone: 'green' },
  on_the_way: { label: 'Yolda', long: 'Kuryede', tone: 'violet' },
  delivered: { label: 'Teslim edildi', long: 'Teslim edildi', tone: 'green' },
  cancelled: { label: 'İptal', long: 'İptal edildi', tone: 'red' },
};

export function StatusBadge({ status }) {
  const s = ORDER_STATUS[status] || { label: status, tone: '' };
  return <span className={`k-badge ${s.tone}`}>{s.label}</span>;
}

export const tl = (n) => `${new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 }).format(Number(n) || 0)} TL`;
